<template>
  <el-dialog :visible.sync="visible" width="min(960px, calc(100vw - 24px))" :close-on-click-modal="false" append-to-body @closed="onClosed">
    <template slot="title">
      <div class="doc-dialog-header">
        <span class="doc-dialog-title">{{ dialogTitle }}</span>
        <div v-if="readonly && form.id" class="doc-dialog-actions">
          <el-button-group>
            <workflow-doc-buttons biz-type="purchaseReturnSettle" :biz-id="form.id" perm="finance:purchaseReturnSettle" :approve-status="String(form.approveStatus || '0')" @approve="approve(form, true)" @unapprove="approve(form, false)" />
            <el-button v-if="isAuth('finance:purchaseReturnSettle:update') || isAuth('finance:purchaseReturnSettle:save')" type="primary" size="small" :disabled="!canEditDoc" @click="editFromView">修改</el-button>
            <el-button v-if="isAuth('finance:purchaseReturnSettle:delete') || isAuth('finance:purchaseReturnSettle:save')" type="danger" size="small" :disabled="!canEditDoc" @click="deleteFromView">删除</el-button>
          </el-button-group>
        </div>
      </div>
    </template>
    <el-form ref="dataForm" :model="form" :rules="readonly ? {} : formRules" :disabled="readonly" label-width="110px">
      <el-form-item label="结算单号" prop="code"><el-input v-model="form.code" :readonly="codeReadonly || !!form.id" :placeholder="codeReadonly ? '保存时自动生成' : ''" /></el-form-item>
      <el-form-item label="结算日期" prop="bizDate">
        <el-date-picker v-model="form.bizDate" type="date" value-format="yyyy-MM-dd" style="width:100%" />
      </el-form-item>
      <el-form-item label="供应商" prop="supplierId">
        <el-input :value="form.partnerName" readonly placeholder="请选择供应商">
          <el-button v-if="!readonly" slot="append" @click="picker = true">选择</el-button>
        </el-input>
      </el-form-item>
      <el-form-item label="金额">
        <el-input :value="amountDisplay" readonly />
      </el-form-item>
      <el-form-item label="退货出库">
        <el-button v-if="!readonly" size="mini" @click="loadLines">加载未结退货</el-button>
        <div v-if="!readonly" class="form-tip">未开票且已有已审核应付的退货出库可在此结算；已开票请用进项红字发票。金额按原应付含税金额自动计算。</div>
        <el-table :data="form.lines || []" border size="mini" style="margin-top:8px">
          <el-table-column prop="billCode" label="出库单" width="140" />
          <el-table-column prop="materialCode" label="编码" width="120" />
          <el-table-column prop="materialName" label="名称" min-width="120" />
          <el-table-column label="原单含税价" width="110" align="right"><template slot-scope="scope">{{ Number(scope.row.unitPrice || 0).toFixed(3) }}</template></el-table-column>
          <el-table-column prop="remainQuantity" label="可结" width="80" />
          <el-table-column label="本次数量" width="160">
            <template slot-scope="scope"><el-input-number v-model="scope.row.quantity" :min="0" :max="Number(scope.row.remainQuantity || 0)" :precision="6" :disabled="readonly" size="mini" /></template>
          </el-table-column>
          <el-table-column label="本次金额" width="110" align="right"><template slot-scope="scope">{{ Number(lineAmounts[scope.$index] || 0).toFixed(3) }}</template></el-table-column>
          <el-table-column v-if="!readonly" label="操作" width="70" align="center">
            <template slot-scope="scope"><el-button type="text" size="mini" @click="form.lines.splice(scope.$index, 1)">移除</el-button></template>
          </el-table-column>
        </el-table>
      </el-form-item>
      <el-form-item label="备注"><el-input v-model="form.remark" type="textarea" :rows="2" /></el-form-item>
    </el-form>
    <span slot="footer">
      <el-button @click="visible = false">关闭</el-button>
      <el-button v-if="!readonly" type="primary" @click="save">确定</el-button>
    </span>
    <SupplierSelector :visible.sync="picker" @select="onPick" />
  </el-dialog>
</template>

<script>
import WorkflowDocButtons from '../../components/WorkflowDocButtons.vue'
import SupplierSelector from '../../components/SupplierSelector.vue'
import request from '../../api/request'
import auth from './financeAuth'

export default {
  name: 'PurchaseReturnSettleAddOrUpdate',
  mixins: [auth],
  components: { WorkflowDocButtons, SupplierSelector },
  data() {
    return {
      visible: false,
      readonly: false,
      codeReadonly: false,
      picker: false,
      form: this.blank(),
      rules: {
        code: [{ required: true, message: '结算单号不能为空', trigger: 'blur' }],
        bizDate: [{ required: true, message: '结算日期不能为空', trigger: 'change' }],
        supplierId: [{ required: true, message: '供应商不能为空', trigger: 'change' }],
      }
    }
  },
  computed: {
    formRules() {
      return this.codeReadonly ? { bizDate: this.rules.bizDate, supplierId: this.rules.supplierId } : this.rules
    },
    lineAmounts() {
      if (this.readonly) return (this.form.lines || []).map(row => Number(row.lineAmount || 0))
      const selected = new Map()
      return (this.form.lines || []).map(row => {
        const sourceId = row.sourceBizLineId || row.stockReturnLineId
        const qty = Number(row.quantity || 0)
        const sourceQty = Number(row.sourceQuantity || 0)
        const sourceAmount = Number(row.sourceAmount || 0)
        const postedQty = Number(row.postedQuantity || 0)
        const postedAmount = Number(row.postedAmount || 0)
        const before = selected.get(sourceId) || 0
        selected.set(sourceId, before + qty)
        if (qty <= 0 || sourceQty <= 0 || before + qty > sourceQty - postedQty + 0.0000001) return 0
        const amountAt = value => value <= 0 ? 0 : value >= sourceQty - postedQty - 0.0000001
          ? sourceAmount - postedAmount : Math.round((sourceAmount * value / sourceQty + Number.EPSILON) * 1000) / 1000
        return Math.round((amountAt(before + qty) - amountAt(before)) * 1000) / 1000
      })
    },
    amountDisplay() {
      const amount = this.readonly ? Number(this.form.amount || 0)
        : this.lineAmounts.reduce((total, amount) => total + amount, 0)
      return amount.toFixed(3)
    },
    dialogTitle() {
      if (this.readonly) return '查看采购退货结算'
      return this.form.id ? '修改采购退货结算' : '新增采购退货结算'
    }
  },
  methods: {
    blank() {
      return { kind: 'purchaseReturnSettle', code: '', bizDate: this.today(), supplierId: '', partnerName: '', amount: 0, remark: '', lines: [] }
    },
    init(id, readonly) {
      this.visible = true
      this.readonly = !!readonly
      this.codeReadonly = false
      this.picker = false
      if (!id) {
        this.form = this.blank()
        this.resolveCode()
        this.$nextTick(() => this.$refs.dataForm && this.$refs.dataForm.clearValidate())
        return
      }
      request.get('/finance/doc/info', { params: { kind: 'purchaseReturnSettle', id } }).then(res => {
        const row = res.data || {}
        this.form = Object.assign({ kind: 'purchaseReturnSettle', lines: [] }, row)
        if (!Array.isArray(this.form.lines)) this.form.lines = []
        this.$nextTick(() => this.$refs.dataForm && this.$refs.dataForm.clearValidate())
      }).catch(() => {})
    },
    onClosed() {
      this.form = this.blank()
      this.readonly = false
      this.codeReadonly = false
    },
    resolveCode() {
      request.post('/base/docNumberRule/resolve', { docType: 'purchaseReturnSettle' }).then(res => {
        const data = res.data || {}
        this.codeReadonly = !!data.codeReadonly
        if (data.previewCode) this.form.code = data.previewCode
        this.$nextTick(() => this.$refs.dataForm && this.$refs.dataForm.clearValidate('code'))
      }).catch(() => {})
    },
    editFromView() {
      if (!this.assertCanEditDoc('edit')) return
      this.readonly = false
    },
    deleteFromView() {
      if (!this.assertCanEditDoc('delete')) return
      this.$confirm('确定删除该采购退货结算单?', '提示', { type: 'warning' }).then(() => {
        request.post('/finance/doc/delete', { kind: 'purchaseReturnSettle', id: this.form.id }).then(() => {
          this.$message.success('删除成功')
          this.visible = false
          this.$emit('refreshDataList')
        }).catch(() => {})
      }).catch(() => {})
    },
    onPick(row) { this.form.supplierId = row.supplierId; this.form.partnerName = row.supplierName; this.loadLines() },
    loadLines() {
      const partnerId = this.form.supplierId
      if (!partnerId) { this.$message.warning('请先选择供应商'); return }
      request.get('/finance/settle/pendingLines', {
        params: { kind: 'purchaseReturnSettle', partnerId, excludeId: this.form.id || '' }
      }).then(res => {
        const selected = new Map((this.form.lines || []).map(row => [row.stockReturnLineId, Number(row.quantity || 0)]))
        this.$set(this.form, 'lines', (res.data || []).map(row => Object.assign({}, row, { quantity: selected.get(row.stockReturnLineId) || 0 })))
      }).catch(() => {})
    },
    save() {
      this.$refs.dataForm.validate(valid => {
        if (!valid) return
        const lines = (this.form.lines || []).filter(row => Number(row.quantity) > 0)
        if (!lines.length) { this.$message.warning('请填写至少一行本次结算数量'); return }
        const amount = Number(this.amountDisplay)
        if (amount <= 0) { this.$message.warning('原应付金额不足，无法计算结算金额'); return }
        request.post('/finance/doc/save', Object.assign({}, this.form, { amount, lines })).then(() => {
          this.$message.success('保存成功')
          this.visible = false
          this.$emit('refreshDataList')
        }).catch(() => {})
      })
    },
    approve(row, pass) {
      const url = pass ? '/finance/doc/approve' : '/finance/doc/unapprove'
      this.$confirm(pass ? '确定审核?' : '确定弃审?', '提示', { type: 'warning' }).then(() => {
        request.post(url, { kind: 'purchaseReturnSettle', id: row.id }).then(() => {
          this.$message.success(pass ? '审核成功' : '弃审成功')
          if (row && this.form && ((row.id && this.form.id === row.id) || (row.voucherId && this.form.voucherId === row.voucherId))) {
            this.$set(this.form, 'approveStatus', pass ? '2' : '0')
            this.readonly = true
          }
          this.$emit('refreshDataList')
        }).catch(() => {})
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.doc-dialog-header { display: flex; align-items: center; justify-content: space-between; padding-right: 28px; }
.doc-dialog-title { font-size: 16px; font-weight: 600; }
.form-tip { margin-top: 6px; color: #909399; font-size: 12px; line-height: 1.5; }
</style>
